import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import type { ApiReconstructedDocumentDetail } from '@avisoma/shared';
import { LazyImage } from '../LazyImage';
import { FullScreenImagePreviewTrigger } from '../FullScreenImagePreview';
import { PagePreviewHoverProvider } from '../pagePreview/usePagePreviewHover';
import { PagePreviewHoverCard } from '../pagePreview/PagePreviewHoverCard';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';
import { ProcessingStatusCells } from '../tableCells';
import { classifyConstituentPage, type ConstituentPageBucket } from '../../lib/classifyConstituentPage';

type FilterKey = 'all' | ConstituentPageBucket;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'errors', label: 'Errors' },
  { key: 'in-progress', label: 'In progress' },
];

export function ConstituentPagesCard({ entity }: { entity: ApiReconstructedDocumentDetail }) {
  const [filter, setFilter] = useState<FilterKey>('all');

  // react-router reuses this component across /reconstructed-documents/:id swaps,
  // so without resetting on id change the prior entity's filter survives onto the
  // next one — sometimes landing the user in an empty "No pages match" view.
  useEffect(() => {
    setFilter('all');
  }, [entity.id]);

  const { counts, visible, visiblePageIds } = useMemo(() => {
    const tally: Record<ConstituentPageBucket, number> = { errors: 0, 'in-progress': 0, done: 0 };
    const tagged = entity.pages.map((p) => {
      const bucket = classifyConstituentPage(p.processing);
      tally[bucket] += 1;
      return { ...p, bucket };
    });
    const visible = filter === 'all' ? tagged : tagged.filter((p) => p.bucket === filter);
    return {
      counts: { all: entity.pages.length, errors: tally.errors, 'in-progress': tally['in-progress'] },
      visible,
      // Ordered ids of the pages currently shown — drives prev/next in the preview dialog.
      visiblePageIds: visible.map((p) => p.pageId),
    };
  }, [entity.pages, filter]);

  if (entity.pages.length === 0) return null;

  return (
    <div className="bg-white dark:bg-zinc-800 dark:border dark:border-zinc-700 shadow sm:rounded-lg p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-gray-700 dark:text-zinc-200">Constituent pages</h2>
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERS.map(({ key, label }) => {
            const count = counts[key as keyof typeof counts];
            const active = filter === key;
            const disabled = count === 0 && key !== 'all';
            return (
              <button
                key={key}
                type="button"
                onClick={() => !disabled && setFilter(key)}
                disabled={disabled}
                className={clsx(
                  'rounded-full px-3 py-0.5 text-xs font-medium border transition-colors',
                  active
                    ? 'bg-blue-600 border-blue-600 text-white dark:bg-blue-500 dark:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800',
                  disabled && 'opacity-40 cursor-not-allowed hover:bg-white dark:hover:bg-zinc-900',
                )}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>
      </div>
      {visible.length === 0 ? (
        <div className="mt-3 text-xs text-gray-500 dark:text-zinc-400">
          No pages match this filter.{' '}
          <button
            type="button"
            onClick={() => setFilter('all')}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Show all
          </button>
        </div>
      ) : (
        <PagePreviewHoverProvider visibleIds={new Set(visiblePageIds)} dismissKey={filter}>
          <div className="mt-3 max-h-[320px] overflow-x-auto overflow-y-hidden">
            <div className="flex flex-row flex-nowrap gap-3 pb-1">
              {visible.map((p) => (
                <PagePreviewHoverCard
                  key={p.pageId}
                  page={{ id: p.pageId }}
                  className="shrink-0 flex flex-col gap-1 w-[150px]"
                >
                <div className="relative group bg-gray-200 dark:bg-zinc-700 rounded-md overflow-hidden shadow-[0_0_10px_rgba(0,0,0,0.1)]">
                  <div className="relative aspect-3/4 max-h-[280px]">
                    <LazyImage src={pagePreviewSrc(p.pageId, { resize: 'x400' })} className="w-full h-full object-cover" />
                    <FullScreenImagePreviewTrigger pageId={p.pageId} pageIds={visiblePageIds} />
                  </div>
                </div>
                <Link
                  to={`/document-pages/${p.pageId}`}
                  className="text-xs text-center text-blue-600 dark:text-blue-400 hover:underline truncate"
                  title={p.pageId}
                >
                  Page {p.position + 1}
                </Link>
                <div className="flex justify-center">
                  <ProcessingStatusCells
                    processing={p.processing}
                    // Constituents of a reconstructed document ARE the (post-segmentation)
                    // segment/leaf pages and the detail payload doesn't carry a per-page
                    // `segmentation` stage, so listing it here only renders a perpetual
                    // pending dot. Segmentation happened upstream; omit it on constituents.
                    steps={['pdfParser', 'metadata', 'embeddings', 'classification', 'dataExtraction']}
                  />
                </div>
                </PagePreviewHoverCard>
              ))}
            </div>
          </div>
        </PagePreviewHoverProvider>
      )}
    </div>
  );
}
